import { useCallback, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import useOrgId from 'hooks/useOrgId.js';
import useDraggable from 'hooks/useDraggable.js';
import { useFirebase } from 'components/Firebase/index.js';
import config from '../../client-config.js';

const CONFIG = {
  SCRIPT_SRC: config.scriptSrc,
  ORG_ID: config.orgId,
  SITE_URL: config.siteUrl,
  SCRT2_URL: config.scrt2Url,
};
const SCRIPT_ID = 'salesforce-script';
const DEPLOYMENT = 'Support_Chat';

let isInitialized = false;

// Shared state for all hook instances
const defaultPosition = { right: 30, bottom: 0 };
let sharedState = {
  isReady: false,
  isOpen: false,
  position: defaultPosition,
  iframeRect: { height: 0, width: 0 },
};
const listeners = new Set();

const setSharedState = (newState) => {
  sharedState = { ...sharedState, ...newState };
  listeners.forEach((listener) => listener(sharedState));
};

const createOrReuseScript = (onLoad) => {
  let script = document.getElementById(SCRIPT_ID);

  if (script?.onload) {
    script.onload = onLoad;
    onLoad();
    return;
  }

  script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.src = CONFIG.SCRIPT_SRC;
  script.type = 'text/javascript';
  script.onload = onLoad;
  document.body.appendChild(script);
};

const useSalesforceChat = () => {
  const [state, setState] = useState(sharedState);
  const firebase = useFirebase();
  const [jwt, setJwt] = useState(null);
  const orgId = useOrgId();
  const uid = useSelector((state) => state.sessionState.authUser?.uid);
  const observerRef = useRef(null);
  const updateIframeRectTimeoutRef = useRef(null);

  useEffect(() => {
    const listener = setState;
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
      if (updateIframeRectTimeoutRef.current) {
        clearTimeout(updateIframeRectTimeoutRef.current);
      }
    };
  }, []);

  const updateIframeRect = useCallback(() => {
    requestAnimationFrame(() => {
      const frame = document.querySelector('.embeddedMessagingFrame[class~="isMaximized"]');
      if (!frame) return;

      const rect = frame.getBoundingClientRect();
      if (rect.height > 0 && rect.width > 0) {
        setSharedState({ iframeRect: { height: rect.height, width: rect.width } });
      }
    });
  }, []);

  const onDrag = useCallback((position) => setSharedState({ position }), []);
  const onDragEnd = useCallback(() => {
    if (updateIframeRectTimeoutRef.current) {
      clearTimeout(updateIframeRectTimeoutRef.current);
    }
    updateIframeRectTimeoutRef.current = setTimeout(updateIframeRect, 50);
  }, [updateIframeRect]);
  const getBounds = useCallback(() => {
    const { width = 0, height = 0 } = sharedState?.iframeRect || {};
    const { innerWidth = 0, innerHeight = 0 } = window;
    return {
      right: innerWidth - width,
      bottom: innerHeight - height - 16, // 16px for drag handle
    };
  }, []);

  const { isDragging, handleMouseDown } = useDraggable({
    initialPosition: state.position,
    onDrag,
    onDragEnd,
    getBounds,
  });

  useEffect(() => {
    if (!isDragging) return;

    const frame = document.querySelector('.embeddedMessagingFrame[class~="isMaximized"]');
    if (frame) frame.style.pointerEvents = 'none';

    return () => {
      const frameOnCleanup = document.querySelector('.embeddedMessagingFrame[class~="isMaximized"]');
      if (frameOnCleanup) frameOnCleanup.style.pointerEvents = 'auto';
    };
  }, [isDragging]);

  useEffect(() => {
    const frame = document.querySelector('.embeddedMessagingFrame[class~="isMaximized"]');
    if (!frame) return;
    frame.style.right = `${state.position.right}px`;
    frame.style.bottom = `${state.position.bottom}px`;
  }, [state.position]);

  useEffect(() => {
    if (state.isOpen) {
      const observer = new MutationObserver(() => {
        updateIframeRect();
      });

      observer.observe(document.body, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: ['style', 'class'],
      });
      observerRef.current = observer;
      setTimeout(updateIframeRect, 50);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [state.isOpen, updateIframeRect]);

  const fetchToken = useCallback(async () => {
    try {
      const agent = await firebase.getAgent();
      const { body } = await agent.post('/salesforcechat/token').send({ orgId });
      setJwt(body.token);
      return body.token;
    } catch (error) {
      console.error('Failed to fetch Salesforce chat token', error);
      return null;
    }
  }, [firebase, orgId]);

  useEffect(() => {
    if (uid && orgId) {
      fetchToken();
    }
  }, [uid, orgId, fetchToken]);

  const initEmbeddedMessaging = useCallback(() => {
    if (!jwt || isInitialized) return;

    isInitialized = true;
    window.embeddedservice_bootstrap.settings.language = 'en_US';
    window.embeddedservice_bootstrap.settings.hideChatButtonOnLoad = true;

    window.addEventListener('onEmbeddedMessagingReady', () => {
      console.log('Received the onEmbeddedMessagingReady event.');
      setSharedState({ isReady: true });

      if (orgId) {
        window.embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields({ Bam_Hub_Org_Id: orgId });
      }

      if (jwt) {
        window.embeddedservice_bootstrap.userVerificationAPI.setIdentityToken({
          identityTokenType: 'JWT',
          identityToken: jwt,
        });
      }
    });

    window.addEventListener('onEmbeddedMessagingWindowMaximized', () => {
      setSharedState({ isOpen: true });
      updateIframeRect();
    });

    window.addEventListener('onEmbeddedMessagingWindowMinimized', () => {
      window.embeddedservice_bootstrap.userVerificationAPI.clearSession().then(() => {
        window.embeddedservice_bootstrap.utilAPI.hideChatButton();
        setSharedState({ isOpen: false });
      });
    });

    window.addEventListener('onEmbeddedMessagingInitError', (err) => {
      console.error('onEmbeddedMessagingInitError', err);
    });

    window.addEventListener('onEmbeddedMessagingIdentityTokenExpired', async () => {
      const newToken = await fetchToken();
      if (newToken) {
        window.embeddedservice_bootstrap.userVerificationAPI.setIdentityToken({
          identityTokenType: 'JWT',
          identityToken: newToken,
        });
      }
    });

    window.embeddedservice_bootstrap.init(
      CONFIG.ORG_ID,
      DEPLOYMENT,
      CONFIG.SITE_URL,
      { scrt2URL: CONFIG.SCRT2_URL },
    );
  }, [jwt, orgId, fetchToken, updateIframeRect]);

  useEffect(() => {
    if (jwt) {
      createOrReuseScript(() => {
        const checkLibraryLoaded = () => {
          if (window.embeddedservice_bootstrap) {
            initEmbeddedMessaging();
          } else {
            setTimeout(checkLibraryLoaded, 10);
          }
        };
        checkLibraryLoaded();
      });
    }
  }, [jwt, initEmbeddedMessaging]);

  const open = () => {
    setSharedState({ position: defaultPosition });
    if (!window.embeddedservice_bootstrap) return;
    window.embeddedservice_bootstrap.utilAPI.showChatButton();
    window.embeddedservice_bootstrap.utilAPI.launchChat().catch((err) => {
      console.error('Failed to launch chat', err);
    });
  };

  const close = useCallback(() => {
    if (!window.embeddedservice_bootstrap) return;
    window.embeddedservice_bootstrap.userVerificationAPI.clearSession();
    window.embeddedservice_bootstrap.utilAPI.hideChatButton();
    setSharedState({ isOpen: false });
  }, []);

  return {
    ...state,
    isInitialized,
    open,
    close,
    handleMouseDown,
  };
};

export default useSalesforceChat;

